import { useSeoMeta, useHead } from '@unhead/vue';

const SITE_URL = 'https://nangmancalendar.com';
const BRAND = '낭만만세력';

const TRACKING_PARAMS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'ref',
  'gclid',
  'fbclid',
]);

export const DEFAULT_DESCRIPTION =
  '무료 사주, 오늘의 운세, 만세력, 사주 분석을 제공하는 천문역법 서비스.';

export const DEFAULT_OG_IMAGE = 'https://nangmancalendar.com/nangmancalendar_banner.jpg';

/**
 * canonical URL 계산.
 * - 추적용 쿼리(utm_*, ref, gclid 등)는 제거
 * - keepQuery에 명시된 키만 유지
 * - 그 외 쿼리는 전부 제거
 *
 * @param {string} fullPath - route.fullPath 또는 상대 경로
 * @param {object} [options]
 * @param {string[]} [options.keepQuery] - 유지할 쿼리 키 목록
 * @returns {string} 절대 URL (origin 포함)
 */
export function canonicalUrl(fullPath, { keepQuery = [] } = {}) {
  const url = new URL(fullPath, SITE_URL);
  const params = new URLSearchParams(url.search);
  for (const key of Array.from(params.keys())) {
    if (TRACKING_PARAMS.has(key) || !keepQuery.includes(key)) {
      params.delete(key);
    }
  }
  url.search = params.toString();
  return url.toString();
}

/**
 * useSeoMeta에 바로 넘길 수 있는 메타 객체 생성.
 * title, description, image는 페이지별로 오버라이드 가능.
 * titleTemplate은 App.vue가 적용하므로 여기 title은 페이지명만.
 * og/twitter title은 template이 적용되지 않아 브랜드명 포함한 풀네임으로 설정.
 *
 * @param {object} params
 * @param {string} params.title - 페이지명 (예: '사주입력')
 * @param {string} [params.description] - 페이지 설명. 생략 시 DEFAULT_DESCRIPTION
 * @param {string} [params.image] - OG/Twitter 이미지 URL. 생략 시 DEFAULT_OG_IMAGE
 * @param {string} params.url - canonical URL (canonicalUrl()의 결과)
 */
export function buildSeoMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  url,
}) {
  const fullTitle = `${title} | ${BRAND}`;
  return {
    title,
    description,
    ogTitle: fullTitle,
    ogDescription: description,
    ogImage: image,
    ogUrl: url,
    twitterTitle: fullTitle,
    twitterDescription: description,
    twitterImage: image,
    canonical: url,
  };
}

/**
 * 정적 페이지용 SEO 메타 적용 헬퍼.
 * useSeoMeta로 표준 메타(title, description, og·twitter 계열)를 적용하고,
 * useSeoMeta가 다루지 않는 link rel canonical은 useHead로 별도 추가.
 *
 * useSeoMeta 입력에 canonical 키를 넘기면 잘못된 meta name canonical로 출력되므로
 * 이 헬퍼가 그 책임 분리를 담당한다.
 */
export function applySeoMeta({ title, description, image, url }) {
  const meta = buildSeoMeta({ title, description, image, url });
  delete meta.canonical;
  useSeoMeta(meta);
  useHead({ link: [{ rel: 'canonical', href: url }] });
}

/**
 * useHead()에 넘길 수 있는 포맷. reactive 분기가 필요한 라우트(nangman-center 등)용.
 * useSeoMeta는 전체 객체를 함수로 감쌀 수 없어 reactive 분기가 어렵기 때문에,
 * 이 경우 useHead(() => buildHeadMeta({...}))로 사용.
 */
export function buildHeadMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  url,
}) {
  const fullTitle = `${title} | ${BRAND}`;
  return {
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: fullTitle },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: url },
      { name: 'twitter:title', content: fullTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
    ],
    link: [{ rel: 'canonical', href: url }],
  };
}

// 한국어 SEO 기준 (가이드 v1.4 권장 32자, description 85자)
// title은 SERP가 픽셀 기준으로 자동 절단하므로 안전 가드만 두고 60자로 완화
const TITLE_MAX = 60;
const DESC_MAX = 85;

/**
 * 본문 HTML에 Schema.org 마크업이 인라인으로 포함되어 있는지 검사.
 * 운영자가 게시글 본문에 JSON-LD 또는 microdata를 직접 작성한 경우,
 * 페이지가 자동 생성하는 Schema와 중복되어 검색엔진 인식이 충돌할 수 있으므로
 * 호출부에서 자동 JSON-LD 생성을 스킵하는 데 사용.
 */
export function hasInlineSchema(html) {
  if (!html) return false;
  return /application\/ld\+json|itemscope\b|itemtype\s*=/i.test(html);
}

/** HTML 태그 제거 + 엔티티/연속 공백 정리 */
export function stripHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/** 길이 초과 시 (max - suffix.length)에서 자르고 suffix 부착 */
export function truncate(str, max, suffix = '...') {
  if (!str) return '';
  if (str.length <= max) return str;
  return str.slice(0, Math.max(0, max - suffix.length)) + suffix;
}

/** HTML 본문에서 첫 번째 <p> 태그의 텍스트만 추출 */
export function extractFirstParagraph(html) {
  if (!html) return '';
  const match = String(html).match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  return match ? stripHtml(match[1]) : '';
}

/**
 * 게시글 description 폴백 (가이드 v1.4 시트 3 규칙):
 *   1) 부제목 → 2) 본문 첫 <p> → 3) 본문 전체 텍스트
 * 85자 초과 시 80자에서 자르고 '...'.
 */
export function buildArticleDescription({ subtitle, html }) {
  const subClean = subtitle ? stripHtml(subtitle) : '';
  const candidate = subClean || extractFirstParagraph(html) || stripHtml(html);
  return truncate(candidate, DESC_MAX) || DEFAULT_DESCRIPTION;
}

/**
 * 게시글 title 빌더: `{게시글 제목} | {게시판명} | 낭만만세력`
 * 32자 초과 시 게시글 제목 부분만 잘라서 '...' 부착.
 */
export function buildArticleTitle(articleTitle, boardName) {
  const cleanTitle = stripHtml(articleTitle);
  const suffix = ` | ${boardName} | ${BRAND}`;
  const room = TITLE_MAX - suffix.length;
  if (room <= 0) return truncate(cleanTitle, TITLE_MAX);
  const head = cleanTitle.length > room ? truncate(cleanTitle, room) : cleanTitle;
  return `${head}${suffix}`;
}

/**
 * 게시글 상세 페이지용 useHead 객체.
 * App.vue의 titleTemplate(`{title} | 낭만만세력`)을 무시하기 위해 titleTemplate=null 설정.
 *
 * @param {object} params
 * @param {string} params.fullTitle - buildArticleTitle()로 만든 풀 제목
 * @param {string} params.description
 * @param {string} [params.image]
 * @param {string} params.url
 * @param {string} [params.publishedAt] - ISO8601
 * @param {string} [params.modifiedAt] - ISO8601
 * @param {object} [params.jsonLd] - Schema.org JSON-LD 객체
 */
export function buildArticleHeadMeta({
  fullTitle,
  description,
  image = DEFAULT_OG_IMAGE,
  url,
  publishedAt,
  modifiedAt,
  jsonLd,
}) {
  const meta = [
    { name: 'description', content: description },
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: description },
    { property: 'og:image', content: image },
    { property: 'og:url', content: url },
    { property: 'og:type', content: 'article' },
    { property: 'og:site_name', content: BRAND },
    { property: 'og:locale', content: 'ko_KR' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: image },
  ];
  if (publishedAt) meta.push({ property: 'article:published_time', content: publishedAt });
  if (modifiedAt) meta.push({ property: 'article:modified_time', content: modifiedAt });

  const head = {
    title: fullTitle,
    titleTemplate: null,
    meta,
    link: [{ rel: 'canonical', href: url }],
  };
  if (jsonLd) {
    head.script = [{ type: 'application/ld+json', innerHTML: JSON.stringify(jsonLd) }];
  }
  return head;
}
